import { AppIcon } from '@/components/ui/app-icon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { iconArrowsRotate } from '@/lib/icons';
import { cn } from '@/lib/utils';
import {
  useCheckMailboxDomainMutation,
  useUpdateEmailAccountMutation,
  type EngageMailbox,
} from '@/store/features/engage.feature';
import { apiErrorMessage } from '../api-error.util';
import { ConnectCard, ConnectFrame } from './connect-mailbox-frame.component';
import {
  CheckIcon,
  DomainRowView,
  StateChip,
} from './domain-record-rows.component';
import { connectProviderFor, domainRows } from './connect-mailbox.util';
import { LimitStepper } from './limit-stepper.component';
import { providerLabel } from './provider.util';
import { ProviderMark } from './provider-mark.component';
import { domainOf } from '../sending-domain.util';

/** SPF, DKIM and DMARC: what was read, and the record to add for a gap. */
function DomainCard({
  mailbox,
  checking,
  failed,
}: {
  mailbox: EngageMailbox;
  checking: boolean;
  failed: boolean;
}) {
  const option = connectProviderFor(mailbox.provider);
  const heading = 'Domain ' + domainOf(mailbox.email_address);
  const rows = domainRows(mailbox.domain_check, option);
  // A personal mail domain has no records of its own to read.
  if (mailbox.domain_check?.status === 'provider' && !checking) return null;
  if (checking || (!rows && !failed))
    return (
      <ConnectCard heading={heading}>
        <ul aria-busy='true'>
          {['SPF', 'DKIM', 'DMARC'].map((label) => (
            <li
              key={label}
              className='flex items-center gap-2.5 px-3.5 py-2.5 text-sm'
            >
              <Skeleton className='size-[15px] rounded-full' />
              <span className='min-w-0 flex-1 text-muted-foreground'>
                {label}
              </span>
              <StateChip state='checking'>Checking</StateChip>
            </li>
          ))}
        </ul>
      </ConnectCard>
    );
  if (!rows)
    return (
      <ConnectCard heading={heading}>
        <p
          role='alert'
          className='flex items-center gap-2.5 px-3.5 py-3 text-sm text-muted-foreground'
        >
          <CheckIcon ok={false} />
          Could not check the domain
        </p>
      </ConnectCard>
    );
  return (
    <ConnectCard heading={heading}>
      <ul>
        {rows.map((row) => (
          <DomainRowView key={row.key} row={row} />
        ))}
      </ul>
    </ConnectCard>
  );
}

/**
 * Checks the new mailbox's domain once as the step opens, and again on
 * request. The fresh answer reaches the mailbox through the list.
 */
function useDomainCheck(mailbox: EngageMailbox | undefined) {
  const [check, { isLoading }] = useCheckMailboxDomainMutation();
  const [failed, setFailed] = useState(false);
  const checked = useRef<string | null>(null);
  const id = mailbox?.id;
  const run = useCallback(
    (mailboxId: string) => {
      setFailed(false);
      check(mailboxId)
        .unwrap()
        .catch(() => setFailed(true));
    },
    [check]
  );
  useEffect(() => {
    if (!id || checked.current === id) return;
    checked.current = id;
    run(id);
  }, [id, run]);
  return {
    checking: isLoading,
    failed,
    again: () => {
      if (id) run(id);
    },
  };
}

/** The new mailbox: signed in, its domain's records, and its daily limit. */
export function ChecksView({
  mailbox,
  doneLabel,
  onDone,
}: {
  /** Undefined while the list brings the mailbox in. */
  mailbox: EngageMailbox | undefined;
  doneLabel: string;
  onDone: () => void;
}) {
  const [update, { isLoading: saving }] = useUpdateEmailAccountMutation();
  const domain = useDomainCheck(mailbox);

  async function setLimit(limit: number) {
    if (!mailbox) return;
    try {
      await update({ id: mailbox.id, daily_limit: limit }).unwrap();
    } catch (error) {
      toast.error(
        apiErrorMessage(error) || 'Could not change the daily limit.'
      );
    }
  }

  const label = mailbox ? providerLabel(mailbox.provider) : '';
  return (
    <ConnectFrame
      label='Check the new mailbox'
      title={
        mailbox ? (
          <span className='flex items-center gap-3'>
            <ProviderMark provider={mailbox.provider} boxed />
            <span className='min-w-0 truncate text-base'>
              {mailbox.email_address}
            </span>
          </span>
        ) : (
          <Skeleton className='h-9 w-56' />
        )
      }
      footer={
        <>
          <Button
            variant='outline'
            size='sm'
            disabled={!mailbox || domain.checking}
            onClick={domain.again}
            className='mr-auto gap-1.5'
          >
            <AppIcon
              icon={iconArrowsRotate}
              className={cn(domain.checking && 'motion-safe:animate-spin')}
              style={{ fontSize: 11 }}
              aria-hidden
            />
            Check again
          </Button>
          <Button size='sm' onClick={onDone}>
            {doneLabel}
          </Button>
        </>
      }
    >
      <div className='space-y-3'>
        <div className='flex items-center gap-2 text-xs text-muted-foreground'>
          <span>{label}</span>
          <Badge variant='success' className='px-2 py-0 text-[10px]'>
            <span className='size-1.5 rounded-full bg-current' aria-hidden />
            Connected
          </Badge>
        </div>
        <ConnectCard>
          <div className='flex items-center gap-2.5 px-3.5 py-2.5 text-sm'>
            <CheckIcon ok />
            Signed in
          </div>
        </ConnectCard>
        {mailbox && (
          <DomainCard
            mailbox={mailbox}
            checking={domain.checking}
            failed={domain.failed}
          />
        )}
        <ConnectCard>
          <div className='flex items-center gap-2.5 px-3.5 py-2 text-sm'>
            <span className='min-w-0 flex-1'>Daily limit</span>
            {mailbox ? (
              <>
                <LimitStepper
                  value={mailbox.daily_limit}
                  disabled={saving}
                  onCommit={(next) => void setLimit(next)}
                  ariaLabel='Daily limit'
                />
                <span className='shrink-0 text-xs text-muted-foreground'>
                  a day
                </span>
              </>
            ) : (
              <Skeleton className='h-8 w-28' />
            )}
          </div>
        </ConnectCard>
      </div>
    </ConnectFrame>
  );
}
